import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, HostListener, Input, Signal, computed, inject, signal } from '@angular/core';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { CommandPaletteService } from '../../../modules/layout/components/command-palette/command-palette.service';
import { PaletteCommand } from '../../../modules/layout/components/command-palette/command-palette.model';
import { KeyboardShortcutsDialogComponent } from '../../../modules/layout/components/command-palette/keyboard-shortcuts-dialog.component';

/** What the launcher needs from the AI assistant host. */
export interface AssistantLauncherTarget {
  showLauncher: Signal<boolean>;
  openAssistant(): void;
}

/** What the launcher needs from the feedback host. */
export interface FeedbackLauncherTarget {
  showLauncher: Signal<boolean>;
  openFeedback(): void;
}

interface LauncherOption {
  id: 'assistant' | 'feedback' | 'shortcuts';
  label: string;
  icon: string;
  open: () => void;
}

const PALETTE_TITLES: Record<LauncherOption['id'], string> = {
  assistant: 'aiAssistant.askTooltip',
  feedback: 'feedbackWidget.launcher.tooltip',
  shortcuts: 'keyboardShortcuts.title',
};

const PALETTE_KEYWORDS: Record<LauncherOption['id'], string[]> = {
  assistant: ['ai', 'assistant', 'help', 'question'],
  feedback: ['feedback', 'bug', 'report', 'help'],
  shortcuts: ['keyboard', 'shortcuts', 'keys', 'help'],
};

/**
 * One floating help button: AI assistant, feedback and the keyboard shortcuts. It opens a small menu,
 * or the only available option directly. Each host keeps its own visibility rules and dialog.
 */
@Component({
  selector: 'app-assistance-launcher',
  standalone: true,
  imports: [CommonModule, TooltipModule, TranslateModule, KeyboardShortcutsDialogComponent],
  templateUrl: './assistance-launcher.component.html',
  styleUrl: './assistance-launcher.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssistanceLauncherComponent {
  @Input({ required: true }) assistant!: AssistantLauncherTarget;

  @Input({ required: true }) feedback!: FeedbackLauncherTarget;

  readonly menuOpen = signal(false);

  readonly shortcutsOpen = signal(false);

  readonly options = computed<LauncherOption[]>(() => {
    const options: LauncherOption[] = [];
    if (this.assistant.showLauncher()) {
      options.push({ id: 'assistant', label: 'aiAssistant.title', icon: 'pi pi-bolt', open: () => this.assistant.openAssistant() });
    }
    if (this.feedback.showLauncher()) {
      options.push({ id: 'feedback', label: 'feedbackWidget.launcher.label', icon: 'pi pi-comment', open: () => this.feedback.openFeedback() });
    }
    // Shortcuts only matter inside the app, i.e. where the other help is offered (logged in).
    if (options.length) {
      options.push({ id: 'shortcuts', label: 'keyboardShortcuts.title', icon: 'pi pi-th-large', open: () => this.shortcutsOpen.set(true) });
    }
    return options;
  });

  constructor(private readonly host: ElementRef<HTMLElement>) {
    // The same options, offered in the command palette under the same visibility rules.
    const unregister = inject(CommandPaletteService).register(() =>
      this.options().map((option): PaletteCommand => ({
        id: `help:${option.id}`,
        group: 'actions',
        title: PALETTE_TITLES[option.id],
        icon: option.icon,
        keywords: PALETTE_KEYWORDS[option.id],
        run: () => option.open(),
      })),
    );
    inject(DestroyRef).onDestroy(unregister);
  }

  /** With a single option there is nothing to choose: open it straight away. */
  toggle(): void {
    const options = this.options();
    if (options.length === 1) {
      options[0].open();
      return;
    }
    this.menuOpen.update((open) => !open);
  }

  choose(option: LauncherOption): void {
    this.menuOpen.set(false);
    option.open();
  }

  trackById(_: number, option: LauncherOption): string {
    return option.id;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // The path is captured at dispatch: clicking the icon swaps it for the cross, so by now
    // the clicked element may already be detached and `contains()` would call it "outside".
    if (this.menuOpen() && !event.composedPath().includes(this.host.nativeElement)) {
      this.menuOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.menuOpen.set(false);
  }
}
